/**
 * Detail screen for a single skill — opened when the user taps a skill
 * row from /skills. URL: /skills/:skillId
 *
 * Mirrors iOS SkillDetailView: big-number header + trend arrow + level
 * label, description, three stat tiles (confidence / signals / trend),
 * and a recent-evidence list.
 */

import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";
import { BackLink } from "@/shared/components/BackLink";

import {
  ArrowDownRight,
  ArrowUpRight,
} from "@/shared/components/icons";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { TrendChart } from "@/shared/components/TrendChart";
import { fetchSkillHistory, fetchSkills } from "@/shared/api";
import { drillSkillRoute } from "@/shared/chatRoute";
import { levelLabel, type SkillState } from "@/shared/models";
import { bandMeta, barFill, clampLevel, daySpan, deltaHeadline } from "@/features/skills/lib/skillStats";

export function SkillDetail() {
  const { skillId } = useParams<{ skillId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["skills"],
    queryFn: fetchSkills,
  });

  if (!skillId) return <Navigate to="/skills" replace />;
  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 text-text-muted">
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 text-danger">
        Couldn't load skill: {error instanceof Error ? error.message : "Unknown error"}
      </div>
    );
  }

  const skill = data?.skills.find((s) => s.skillId === skillId);
  if (!skill) {
    return <Navigate to="/skills" replace />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <BackLink to="/skills" label="Back to Skills" />

      <SkillHeader skill={skill} />
      {skill.description && (
        <p className="text-text-muted text-sm leading-relaxed">
          {skill.description}
        </p>
      )}
      <StatsBlock skill={skill} />
      <SkillTrajectory skillId={skill.skillId} />
      <EvidenceList skill={skill} />

      <DrillThisSkillButton skill={skill} />
    </div>
  );
}

function SkillHeader({ skill }: { skill: SkillState }) {
  const unmapped = skill.signalCount === 0;
  const level = clampLevel(skill.level);
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-4">
        <span className="text-5xl font-bold tabular-nums">
          {unmapped ? "–" : level}
        </span>
        <div className="flex-1 pb-2">
          <div
            className="text-lg font-semibold text-text"
            style={unmapped ? undefined : { color: bandMeta(level).color }}
          >
            {unmapped ? "Unmapped" : levelLabel(level)}
          </div>
          <div className="text-sm text-text-muted">{skill.category}</div>
        </div>
        {!unmapped && skill.trend !== "steady" && (
          <TrendBadge trend={skill.trend} />
        )}
      </div>
      <h1 className="text-xl font-semibold text-text">{skill.name}</h1>
      <div className="h-2 bg-border rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(2, level)}%`, backgroundColor: barFill(level, unmapped) }}
        />
      </div>
    </div>
  );
}

function TrendBadge({ trend }: { trend: SkillState["trend"] }) {
  // Steady gets no badge — only render for movement.
  if (trend === "steady") return null;
  const Icon = trend === "up" ? ArrowUpRight : ArrowDownRight;
  const cls =
    trend === "up"
      ? "text-green-400 bg-green-400/15"
      : "text-red-400 bg-red-400/15";
  return (
    <span
      className={`px-2 py-2 rounded-md ${cls}`}
      aria-label={`Trend: ${trend}`}
    >
      <Icon size={18} strokeWidth={2} />
    </span>
  );
}

function StatsBlock({ skill }: { skill: SkillState }) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      <StatTile label="Confidence" value={`${skill.confidence}%`} />
      <StatTile label="Signals" value={`${skill.signalCount}`} />
      <StatTile label="Trend" value={skill.trend} capitalize />
    </div>
  );
}

function StatTile({
  label,
  value,
  capitalize,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3 py-3 text-center">
      <div className={`text-lg font-semibold text-text ${capitalize ? "capitalize" : ""}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mt-0.5">
        {label}
      </div>
    </div>
  );
}

function SkillTrajectory({ skillId }: { skillId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["skill-history", skillId],
    queryFn: () => fetchSkillHistory(skillId),
  });

  const points = data?.points ?? [];
  // Need a drawable line (≥2 points) AND at least 2 signals — a single signal
  // makes a technically-valid but meaningless "trend".
  const enoughSignal = (points[points.length - 1]?.signalCount ?? 0) >= 2;
  if (isLoading || points.length < 2 || !enoughSignal) return null;

  const first = points[0];
  const last = points[points.length - 1];
  const headline = deltaHeadline(first.level, last.level, daySpan(first.date, last.date));

  return (
    <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
      <h2 className={`text-sm font-semibold ${headline.cls}`}>{headline.text}</h2>
      <TrendChart
        points={points.map((p) => ({ date: p.date, value: p.level }))}
        height={100}
        showAxis
        ariaLabel="This skill's level over time"
      />
    </div>
  );
}

function EvidenceList({ skill }: { skill: SkillState }) {
  if (skill.recentEvidence.length === 0) {
    return (
      <div className="text-sm text-text-muted">
        {skill.signalCount === 0
          ? "No evidence yet. Drill this skill to get a read."
          : "No recent evidence to surface."}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
      <h2 className="text-sm font-semibold text-text">Recent evidence</h2>
      <ul className="space-y-2">
        {skill.recentEvidence.map((phrase, idx) => (
          <li key={idx} className="flex gap-2 text-sm text-text">
            <span className="text-text-muted/60 mt-1.5">•</span>
            <span className="flex-1">{phrase}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DrillThisSkillButton({ skill }: { skill: SkillState }) {
  return (
    <DrillLaunchLink
      to={drillSkillRoute(skill)}
      className="block w-full text-center py-3 rounded-lg bg-accent text-white font-medium text-sm hover:bg-accent/90 transition"
    >
      Drill {skill.name.toLowerCase()}
    </DrillLaunchLink>
  );
}
